<script setup lang="ts">
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-vue-next';
import { computed } from 'vue';

import {
    MCP_ACTION_LABELS,
    MCP_STATE_CLASSES,
    MCP_STATE_LABELS,
    MCPConnectorConfig
} from '@/components/settingsMenu/sections/mcp-connector-ui/mcpConnectorConfig';
import { Button } from '@/components/ui/button';
import type { ConnectorState } from '@/services/mcpConnectors';

const props = defineProps<{
    connector: ConnectorState;
    isPending: boolean;
    /** Tool permissions only exist for a live connection, so only it can expand. */
    isExpandable: boolean;
    isExpanded: boolean;
}>();

const emit = defineEmits<{
    action: [];
    toggle: [];
}>();

const config = computed(() => MCPConnectorConfig[props.connector.provider]);
</script>

<template>
    <div class="connector-item flex items-center justify-between gap-4">
        <div class="connector-identity flex min-w-0 items-center gap-4">
            <div
                class="connector-logo h-8 w-8 shrink-0 rounded-sm overflow-hidden"
            >
                <img
                    :src="config.logo"
                    :alt="config.name"
                    class="h-full w-full object-cover"
                />
            </div>
            <div class="min-w-0">
                <h4 class="connector-name text-sm font-medium">
                    {{ config.name }}
                </h4>
                <!-- Keeps carrying the connection state, including the red one: a
                     dead connection is the thing this line exists to surface. -->
                <p class="text-xs" :class="MCP_STATE_CLASSES[connector.state]">
                    {{ MCP_STATE_LABELS[connector.state] }}
                </p>
            </div>
        </div>
        <div class="flex shrink-0 items-center gap-2">
            <!-- Named rather than a bare chevron: nothing announced that the row
                 opened onto anything. A link so it does not read as a second action
                 competing with connect/disconnect. -->
            <Button
                v-if="isExpandable"
                size="sm"
                variant="link"
                class="text-xs"
                @click="emit('toggle')"
            >
                Edit tools permissions
                <component
                    :is="isExpanded ? ChevronDown : ChevronRight"
                    class="ml-1 h-3 w-3"
                />
            </Button>
            <Button
                size="sm"
                variant="outline"
                :disabled="isPending"
                @click="emit('action')"
            >
                <Loader2 v-if="isPending" class="mr-2 h-4 w-4 animate-spin" />
                {{ MCP_ACTION_LABELS[connector.state] }}
            </Button>
        </div>
    </div>
</template>
